import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Boxes,
  ShoppingCart,
  AlertTriangle,
  Wrench,
  Calendar,
  Clock,
  PackageOpen,
  Truck,
  Tag,
  ListChecks,
  CreditCard,
  DollarSign,
  Trash2,
  Link2,
  Facebook,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { apiGet } from "../api/client";
import { Badge } from "../ui/Badge";
import { useAuth } from "../auth/AuthContext";
import { cn } from "../ui/cn";

const NAV = [
  { group: "Overview", items: [
    { id: "home", label: "Dashboard", icon: Home, path: "/admint" },
  ]},
  { group: "Inventory", items: [
    { id: "inventory", label: "Inventory", icon: Boxes, path: "/admint/inventory" },
    { id: "fb-candidates", label: "FB Candidates", icon: Facebook, path: "/admint/fb-candidates" },
    { id: "fb-queue", label: "FB Queue", icon: Facebook, path: "/admint/fb-queue", badgeKey: "fbQueued" },
    { id: "unfulfilled", label: "Unfulfilled", icon: AlertTriangle, path: "/admint/unfulfilled-sales" },
    { id: "manual", label: "Manual Updates", icon: Wrench, path: "/admint/manual-updates" },
    { id: "failed-listings", label: "Failed Listings", icon: AlertTriangle, path: "/admint/failed-listings", badgeKey: "failed" },
    { id: "trash", label: "Trash Candidates", icon: Trash2, path: "/admint/trash" },
  ]},
  { group: "Sales", items: [
    { id: "sales", label: "Sales", icon: ShoppingCart, path: "/admint/sales" },
    { id: "funds", label: "Funds", icon: DollarSign, path: "/admint/funds" },
    { id: "pnl", label: "P&L statement", icon: DollarSign, path: "/admint/pnl" },
    { id: "expenses", label: "Expenses", icon: DollarSign, path: "/admint/expenses" },
    { id: "listings", label: "Listings", icon: Tag, path: "/admint/listings" },
    { id: "shipments", label: "Shipments", icon: Truck, path: "/admint/shipments" },
    { id: "returns", label: "Returns", icon: PackageOpen, path: "/admint/returns" },
    { id: "match", label: "Match Orders", icon: Link2, path: "/admint/match-orders", badgeKey: "orphans" },
  ]},
  { group: "Bidding", items: [
    { id: "scheduled", label: "Scheduled", icon: Calendar, path: "/admint/scheduled-bids" },
    { id: "upcoming", label: "Upcoming", icon: Clock, path: "/admint/upcoming" },
  ]},
  { group: "Operations", items: [
    { id: "tasks", label: "Tasks", icon: ListChecks, path: "/admint/tasks", badgeKey: "tasks" },
    { id: "subscriptions", label: "Subscriptions", icon: CreditCard, path: "/admint/subscriptions" },
  ]},
];

export default function SideNav({ onOpenPalette }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [orphanCount, setOrphanCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [fbQueuedCount, setFbQueuedCount] = useState(0);

  const isPathSelected = (path) => {
    const p = location.pathname;
    if (path === "/admint") return p === "/admint" || p === "/admint/";
    return p === path || p.startsWith(path + "/");
  };

  const refreshBadges = useCallback(async (signal) => {
    try {
      const [tasksJs, pendingJs, failedJs, fbStatusJs] = await Promise.allSettled([
        apiGet("/api/tasks", { signal }),
        apiGet("/api/pending_sales", { signal }),
        apiGet("/api/failed_listings", { signal }),
        apiGet("/api/fb_queue_status", { signal }),
      ]);
      if (tasksJs.status === "fulfilled") {
        const v = tasksJs.value;
        setTaskCount(Number(v?.count ?? (Array.isArray(v?.tasks) ? v.tasks.length : 0)) || 0);
      }
      if (pendingJs.status === "fulfilled") {
        const v = pendingJs.value;
        setOrphanCount(Number(v?.count ?? (Array.isArray(v?.results) ? v.results.length : 0)) || 0);
      }
      if (failedJs.status === "fulfilled") {
        const v = failedJs.value;
        setFailedCount(Number(v?.count ?? (Array.isArray(v?.results) ? v.results.length : 0)) || 0);
      }
      if (fbStatusJs.status === "fulfilled") {
        const v = fbStatusJs.value;
        const c = v?.counts || {};
        setFbQueuedCount(Number(c.queued || 0) + Number(c.error || 0));
      }
    } catch {
      // swallow — badges are best-effort
    }
  }, []);

  // Badge poll cadence. The badges (pending sales, failed listings,
  // FB queue, tasks) feed off endpoints that can transitively touch
  // eBay's rate-limited Fulfillment API, so we keep the heartbeat slow
  // (5 minutes) and lean on focus + visibilitychange events to refresh
  // when the operator actually returns to the tab. Manual refresh is
  // available on each page via its own "Refresh" button.
  useEffect(() => {
    const ctrl = new AbortController();
    refreshBadges(ctrl.signal);
    const onFocus = () => {
      // Only refresh on focus if the tab is currently visible.
      if (document.visibilityState === "visible") refreshBadges();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshBadges();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const id = window.setInterval(() => {
      // Skip the heartbeat entirely while the tab is in the background
      // — saves quota when the operator leaves the page open for hours.
      if (document.visibilityState === "visible") refreshBadges();
    }, 5 * 60 * 1000);
    return () => {
      ctrl.abort();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(id);
    };
  }, [refreshBadges]);

  const badges = { tasks: taskCount, orphans: orphanCount, failed: failedCount, fbQueued: fbQueuedCount };

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-40 flex flex-col",
        "bg-bg-surface border-r border-border",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-[72px]" : "w-[252px]"
      )}
    >
      {/* Brand */}
      <div className={cn("h-14 px-3 flex items-center gap-2 border-b border-border shrink-0",
        collapsed && "justify-center px-2")}>
        <div className="h-8 w-8 rounded-lg bg-brand-500 grid place-items-center text-white font-semibold shrink-0">
          B
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-serif text-[1.05rem] text-ink truncate leading-none">BidRL</span>
            <span className="text-[11px] text-ink-subtle truncate mt-0.5">Operator console</span>
          </div>
        )}
      </div>

      {/* Search button (opens palette) */}
      <div className="p-2.5">
        <button
          type="button"
          onClick={onOpenPalette}
          className={cn(
            "w-full inline-flex items-center gap-2 px-2.5 h-9 rounded-lg",
            "bg-bg-subtle border border-border text-ink-muted text-sm",
            "hover:bg-bg hover:border-border-strong hover:text-ink transition-colors",
            collapsed && "justify-center px-0"
          )}
          aria-label="Open command palette"
        >
          <Search size={16} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Search…</span>
              <span className="hidden sm:flex items-center gap-0.5 text-[10px] text-ink-subtle">
                <kbd className="px-1 py-0.5 rounded border border-border bg-white">⌘</kbd>
                <kbd className="px-1 py-0.5 rounded border border-border bg-white">K</kbd>
              </span>
            </>
          )}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 pb-2 space-y-3">
        {NAV.map((section) => (
          <div key={section.group}>
            {!collapsed && (
              <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-ink-subtle font-semibold">
                {section.group}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isPathSelected(item.path);
                const badge = item.badgeKey ? badges[item.badgeKey] : 0;
                return (
                  <li key={item.id} className="relative">
                    <button
                      type="button"
                      onClick={() => navigate(item.path)}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "w-full inline-flex items-center gap-2.5 h-9 rounded-lg px-2 text-sm",
                        "transition-colors",
                        active
                          ? "bg-brand-50 text-brand-700 font-medium"
                          : "text-ink-muted hover:text-ink hover:bg-bg-subtle",
                        collapsed && "justify-center px-0"
                      )}
                    >
                      <span className={cn(
                        "shrink-0 grid place-items-center h-6 w-6 rounded-md",
                        active ? "text-brand-500" : "text-ink-subtle"
                      )}>
                        <Icon size={16} />
                      </span>
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left truncate">{item.label}</span>
                          {!!badge && (
                            <Badge tone="warning" size="xs">{badge}</Badge>
                          )}
                        </>
                      )}
                    </button>
                    {active && (
                      <span
                        className="absolute pointer-events-none w-1 h-5 rounded-r-full bg-brand-500 left-0 top-1/2 -translate-y-1/2"
                        aria-hidden
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-2.5 flex flex-col gap-1">
        <button
          type="button"
          onClick={() => navigate("/admint/settings")}
          title={collapsed ? "Settings" : undefined}
          className={cn(
            "w-full inline-flex items-center gap-2.5 h-9 rounded-lg px-2 text-sm",
            "text-ink-muted hover:text-ink hover:bg-bg-subtle transition-colors",
            isPathSelected("/admint/settings") && "bg-bg-subtle text-ink",
            collapsed && "justify-center px-0"
          )}
        >
          <Settings size={16} />
          {!collapsed && <span className="flex-1 text-left">Settings</span>}
        </button>
        <button
          type="button"
          onClick={logout}
          title={collapsed ? "Sign out" : undefined}
          className={cn(
            "w-full inline-flex items-center gap-2.5 h-9 rounded-lg px-2 text-sm",
            "text-ink-muted hover:text-ink hover:bg-bg-subtle transition-colors",
            collapsed && "justify-center px-0"
          )}
        >
          <LogOut size={16} />
          {!collapsed && <span className="flex-1 text-left">Sign out</span>}
        </button>
        {!collapsed && user?.username && (
          <div className="mt-1 px-2 py-1.5 rounded-lg border border-border bg-bg-subtle text-xs">
            <div className="text-ink truncate">{user.username}</div>
            <div className="text-ink-subtle">Signed in</div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="mt-1 self-end h-7 w-7 rounded-md text-ink-subtle hover:text-ink hover:bg-bg-subtle grid place-items-center transition-colors"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>
    </aside>
  );
}

export const SIDENAV_DEFAULT_WIDTH = 252;
export const SIDENAV_COLLAPSED_WIDTH = 72;
